import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { motion } from "framer-motion"
import { AppWindow, ArrowUpRight, Send, Smartphone } from "lucide-react"
import { createContext, type ReactNode, useContext } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
	ANALYTICS_PERIODS,
	type AnalyticsPeriod,
	getAudienceAnalytics,
	getContentAnalytics,
} from "@/entities/analytics"
import { useSessionRole } from "@/entities/session"
import { formatShortDate } from "@/shared/lib/format-date"
import { fadeUp, fetchingDim, staggerContainer } from "@/shared/lib/motion"
import { useIntro, useShownKey } from "@/shared/lib/use-shown-key"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { BarList, BarListSkeleton } from "@/shared/ui/charts/bar-list"
import { LineChart, LineChartSkeleton } from "@/shared/ui/charts/line-chart"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { LoadSwap } from "@/shared/ui/load-swap"
import { PageShell } from "@/shared/ui/page-shell"
import { Skeleton } from "@/shared/ui/skeleton"
import { SlidingPill } from "@/shared/ui/sliding-pill"

const number = new Intl.NumberFormat("ru-RU")
const percent = (value: number | null | undefined) =>
	value === null || value === undefined ? "нет данных" : `${Math.round(value * 100)}%`

const PERIOD_LABEL: Record<string, string> = {
	"7": "7 дней",
	"30": "30 дней",
	"90": "90 дней",
	"365": "Год",
	all: "Всё время",
}

const SOURCE_LABEL: Record<string, string> = {
	tma: "Мини-апп в Telegram",
	bot: "Код входа из бота навигации",
	ios: "iOS, регистрация по почте",
	android: "Android, регистрация по почте",
	email: "Регистрация по почте",
}

function periodFrom(value: string | null): AnalyticsPeriod {
	const days = Number(value)
	return (ANALYTICS_PERIODS as readonly number[]).includes(days)
		? (days as AnalyticsPeriod)
		: value === "all"
			? null
			: 30
}

/** Каскад плиток только на первом показе: смену периода ведёт переход всего блока. */
const IntroContext = createContext(true)

function Tile({
	label,
	value,
	hint,
	children,
	href,
}: {
	label: string
	value: string
	hint?: ReactNode
	children?: ReactNode
	href?: string
}) {
	const body = (
		<>
			<p className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
				{label}
				{href ? (
					<ArrowUpRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
				) : null}
			</p>
			<p className="mt-1.5 text-[28px] leading-none font-semibold tracking-tight tabular-nums">
				{value}
			</p>
			{hint ? <p className="mt-2 text-xs leading-snug text-muted-foreground">{hint}</p> : null}
			{children}
		</>
	)
	const intro = useContext(IntroContext)
	return (
		<motion.div variants={intro ? fadeUp : undefined}>
			{href ? (
				<Link
					to={href}
					className="group block h-full rounded-2xl border border-border bg-card p-4 transition-colors hover:border-foreground/20"
				>
					{body}
				</Link>
			) : (
				<div className="h-full rounded-2xl border border-border bg-card p-4">{body}</div>
			)}
		</motion.div>
	)
}

function Panel({
	title,
	description,
	children,
	className,
}: {
	title: string
	description?: string
	children: ReactNode
	className?: string
}) {
	const intro = useContext(IntroContext)
	return (
		<motion.section
			variants={intro ? fadeUp : undefined}
			className={cn("rounded-2xl border border-border bg-card p-5", className)}
		>
			<header className="mb-4">
				<h3 className="font-semibold">{title}</h3>
				{description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
			</header>
			{children}
		</motion.section>
	)
}

function SectionTitle({ children }: { children: ReactNode }) {
	return <h2 className="mt-10 mb-4 text-lg font-semibold tracking-tight first:mt-0">{children}</h2>
}

function Grid({ children, className }: { children: ReactNode; className?: string }) {
	const intro = useContext(IntroContext)
	return (
		<motion.div
			variants={intro ? staggerContainer(0.04) : undefined}
			initial={intro ? "hidden" : false}
			animate={intro ? "visible" : undefined}
			className={cn("grid gap-3", className)}
		>
			{children}
		</motion.div>
	)
}

/** Колонка секции: сама запускает появление детей, которые стоят вне сетки. */
function Stack({ children }: { children: ReactNode }) {
	const intro = useContext(IntroContext)
	return (
		<motion.div
			variants={intro ? staggerContainer(0.04) : undefined}
			initial={intro ? "hidden" : false}
			animate={intro ? "visible" : undefined}
			className="space-y-3"
		>
			{children}
		</motion.div>
	)
}

/** Заглушка плитки: подпись, крупное число и пояснение в несколько строк, как у самой плитки. */
function TileSkeleton({ hint = 0, icon = false }: { hint?: number; icon?: boolean }) {
	return (
		<div className="rounded-2xl border border-border bg-card p-4">
			<div className="flex h-4 items-center">
				<Skeleton className="h-3 w-24 rounded" />
			</div>
			<Skeleton className="mt-1.5 h-7 w-20 rounded-lg" />
			{hint ? (
				<div className="mt-2">
					{Array.from({ length: hint }, (_, index) => (
						<div key={index} className="flex h-[16.5px] items-center">
							<Skeleton
								className="h-2.5 rounded"
								style={{ width: index === hint - 1 ? "60%" : "100%" }}
							/>
						</div>
					))}
				</div>
			) : null}
			{icon ? <Skeleton className="mt-3 size-4 rounded" /> : null}
		</div>
	)
}

/** Заглушка панели: заголовок с пояснением и тело. */
function PanelSkeleton({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<div className={cn("rounded-2xl border border-border bg-card p-5", className)}>
			<div className="mb-4">
				<div className="flex h-6 items-center">
					<Skeleton className="h-4 w-36 rounded" />
				</div>
				<div className="mt-0.5 flex h-5 items-center">
					<Skeleton className="h-3.5 w-56 max-w-full rounded" />
				</div>
			</div>
			{children}
		</div>
	)
}

function AudienceSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-3">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
				{[0, 1, 2, 3, 4].map((index) => (
					<TileSkeleton key={index} hint={4} />
				))}
			</div>
			<div className="grid gap-3 lg:grid-cols-3">
				<PanelSkeleton className="lg:col-span-2">
					<LineChartSkeleton />
				</PanelSkeleton>
				<PanelSkeleton>
					<BarListSkeleton rows={4} />
					<Skeleton className="mt-3 h-16 w-full rounded-lg" />
				</PanelSkeleton>
			</div>
			<div className="grid gap-3 lg:grid-cols-2">
				<PanelSkeleton>
					<BarListSkeleton rows={4} />
				</PanelSkeleton>
				<PanelSkeleton>
					<Skeleton className="h-[88px] w-full rounded-lg" />
				</PanelSkeleton>
			</div>
		</div>
	)
}

function ContentSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-3">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
				{[0, 1, 2].map((index) => (
					<TileSkeleton key={index} icon />
				))}
				<TileSkeleton hint={1} />
				<TileSkeleton hint={1} />
			</div>
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
				{[0, 1, 2, 3, 4].map((index) => (
					<TileSkeleton key={index} hint={3} />
				))}
			</div>
			<PanelSkeleton>
				<LineChartSkeleton />
			</PanelSkeleton>
			<div className="grid gap-3 lg:grid-cols-5">
				<PanelSkeleton className="lg:col-span-3">
					<div className="space-y-2">
						{Array.from({ length: 10 }, (_, index) => (
							<Skeleton key={index} className="h-10 w-full rounded-lg" />
						))}
					</div>
				</PanelSkeleton>
				<PanelSkeleton className="lg:col-span-2">
					<BarListSkeleton rows={8} />
				</PanelSkeleton>
			</div>
		</div>
	)
}

export function AnalyticsPage() {
	const { isAdmin } = useSessionRole()
	const [params, setParams] = useSearchParams()
	const period = periodFrom(params.get("period"))
	const audience = useQuery({
		queryKey: ["analytics", "audience", period],
		queryFn: () => getAudienceAnalytics(period),
		enabled: isAdmin,
		placeholderData: (previous) => previous,
	})
	const content = useQuery({
		queryKey: ["analytics", "content", period],
		queryFn: () => getContentAnalytics(period),
		enabled: isAdmin,
		placeholderData: (previous) => previous,
	})

	// Переход вешаем на период, данные которого реально на экране: пока идёт запрос, виден
	// прежний период, и он не должен заново проявляться.
	const audienceKey = useShownKey(String(period), audience.isPlaceholderData)
	const contentKey = useShownKey(String(period), content.isPlaceholderData)
	const audienceIntro = useIntro(audienceKey, !!audience.data)
	const contentIntro = useIntro(contentKey, !!content.data)

	if (!isAdmin) return <AccessDenied title="Аналитика" />

	const step = audience.data?.period.step ?? "day"
	const formatDate = (date: string) =>
		format(new Date(`${date}T00:00:00`), step === "week" ? "d MMM" : "d MMM", { locale: ru })
	const periodWord = period ? `за ${PERIOD_LABEL[String(period)]!.toLowerCase()}` : "за всё время"

	const a = audience.data
	const c = content.data

	return (
		<PageShell
			eyebrow="Бизнес"
			title="Аналитика"
			subtitle="Как растёт аудитория, что смотрят и сколько выходит материалов."
			action={
				<div
					role="tablist"
					aria-label="Период"
					className="hide-scrollbar relative flex max-w-full gap-1 overflow-x-auto rounded-full bg-secondary/60 p-1"
				>
					<SlidingPill className="rounded-full" />
					{[...ANALYTICS_PERIODS.map(String), "all"].map((value) => {
						const active = (period === null ? "all" : String(period)) === value
						return (
							<button
								key={value}
								type="button"
								role="tab"
								aria-selected={active}
								data-active={active}
								onClick={() => setParams(value === "30" ? {} : { period: value })}
								className={cn(
									"relative shrink-0 rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
									active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
								)}
							>
								<span className="relative">{PERIOD_LABEL[value]}</span>
							</button>
						)
					})}
				</div>
			}
		>
			<div className={fetchingDim(audience.isFetching || content.isFetching)}>
				<SectionTitle>Аудитория</SectionTitle>
				{/* Под аудиторией идёт контент: высота тянется к новой, и он не прыгает. */}
				<LoadSwap
					loading={!a && !audience.isError}
					id={audience.isError ? "error" : `audience-${audienceKey}`}
				>
					{audience.isError ? (
						<LoadErrorState onRetry={() => void audience.refetch()} />
					) : !a ? (
						<AudienceSkeleton />
					) : (
						<IntroContext.Provider value={audienceIntro}>
							<Stack>
								<Grid className="grid-cols-2 lg:grid-cols-5">
									<Tile
										label="Все пользователи"
										value={number.format(a.totals.users)}
										hint={`уникальные аккаунты за всё время, в том числе вышедшие из канала · +${number.format(a.totals.newUsers)} ${periodWord}`}
									/>
									<Tile
										label="В канале сейчас"
										value={
											a.membership.channelTotal === null
												? "нет данных"
												: number.format(a.membership.channelTotal)
										}
										hint={
											a.membership.channelTotal === null
												? "Telegram не ответил, обновите страницу позже"
												: `все участники по Telegram, включая админов и ботов · ${number.format(a.membership.inChannel)} из них заходили в мини-апп`
										}
									/>
									<Tile
										label="Смотрели материалы"
										value={number.format(a.totals.readers)}
										hint={`уникальные люди ${periodWord}, открывшие хотя бы один`}
									/>
									<Tile
										label="Активные"
										value={number.format(a.totals.mau)}
										hint={`уникальные зрители за 30 дней · ${number.format(a.totals.wau)} за неделю · ${number.format(a.totals.dau)} за день`}
									/>
									<Tile
										label="Активация новичков"
										value={percent(a.totals.activation)}
										hint={`доля новых аккаунтов ${periodWord}, открывших хоть один материал`}
									/>
								</Grid>
								<Grid className="lg:grid-cols-3">
									<Panel
										title="Рост аудитории"
										description={`Уникальные люди по ${step === "week" ? "неделям" : "дням"}`}
										className="lg:col-span-2"
									>
										<LineChart
											data={a.series}
											series={[
												{ key: "registrations", label: "Завели аккаунт" },
												{ key: "firstReads", label: "Впервые открыли любой материал" },
											]}
											formatDate={formatDate}
											ariaLabel="Новые аккаунты и первые просмотры по времени"
										/>
									</Panel>
									<Panel
										title="Кто в системе"
										description="Аккаунты и участники канала, каждый посчитан один раз"
									>
										<BarList
											items={[
												{
													key: "in",
													label: "В канале клуба сейчас",
													value: a.membership.inChannel,
												},
												...(a.membership.channelTotal === null
													? []
													: [
															{
																key: "never",
																label: "В канале, но ни разу не открывали мини-апп: аккаунта нет",
																value: Math.max(
																	0,
																	a.membership.channelTotal - a.membership.inChannel,
																),
															},
														]),
												{
													key: "out",
													label: "Telegram есть, в канале нет: вышли или не вступали",
													value: a.membership.notInChannel,
												},
												{
													key: "none",
													label: "Без Telegram: вход только по почте",
													value: a.membership.withoutTelegram,
												},
											]}
										/>
										<p className="mt-3 text-xs text-muted-foreground">
											Членство аккаунтов проверяется раз в сутки
											{a.membership.checkedAt
												? `, последний раз ${formatShortDate(a.membership.checkedAt)}`
												: ""}
											. Тех, кто в канале, но без аккаунта, считаем как разницу с числом участников
											по Telegram: туда входят и админы с ботами.
										</p>
									</Panel>
								</Grid>
								<Grid className="lg:grid-cols-2">
									<Panel title="Откуда приходят" description={`Где завели аккаунт ${periodWord}`}>
										<BarList
											items={a.sources.map((row) => ({
												key: row.source ?? "none",
												label: SOURCE_LABEL[row.source ?? ""] ?? row.source ?? "Не указано",
												value: row.users,
											}))}
											empty="За этот период регистраций не было"
										/>
									</Panel>
									<Panel title="Возвращаемость" description="Сколько зрителей заходят снова">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<p className="text-xs text-muted-foreground">Заходят каждый день</p>
												<p className="mt-1 text-2xl font-semibold tabular-nums">
													{percent(a.totals.stickiness)}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													уникальные зрители за день среди зрителей за 30 дней
												</p>
											</div>
											<div>
												<p className="text-xs text-muted-foreground">Перечитывают материалы</p>
												<p className="mt-1 text-2xl font-semibold tabular-nums">
													{percent(c?.totals.returnRate)}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													доля открытий, к которым человек вернулся ещё раз
												</p>
											</div>
										</div>
									</Panel>
								</Grid>
							</Stack>
						</IntroContext.Provider>
					)}
				</LoadSwap>

				<SectionTitle>Контент</SectionTitle>
				<LoadSwap
					loading={!c && !content.isError}
					id={content.isError ? "error" : `content-${contentKey}`}
				>
					{content.isError ? (
						<LoadErrorState onRetry={() => void content.refetch()} />
					) : !c ? (
						<ContentSkeleton />
					) : (
						<IntroContext.Provider value={contentIntro}>
							<Stack>
								<Grid className="grid-cols-2 lg:grid-cols-5">
									{(
										[
											["telegram", "В канале", Send],
											["tma", "В мини-аппе", AppWindow],
											["mobile", "В мобильном", Smartphone],
										] as const
									).map(([key, label, Icon]) => (
										<Tile
											key={key}
											label={label}
											value={number.format(c.publishing.live[key])}
											href={`/admin?status=published&platform=${key}`}
										>
											<Icon className="mt-3 size-4 text-muted-foreground" />
										</Tile>
									))}
									<Tile
										label="Черновики"
										value={number.format(c.publishing.drafts)}
										hint="нигде не опубликованы"
										href="/admin?status=draft"
									/>
									<Tile
										label="Выпущено в канал"
										value={number.format(c.publishing.sentToChannel)}
										hint={`${periodWord} · запланировано ${number.format(c.publishing.scheduled)}`}
									/>
								</Grid>
								<Grid className="grid-cols-2 lg:grid-cols-5">
									<Tile
										label="Зрители"
										value={number.format(c.totals.people)}
										hint={`уникальные люди ${periodWord}, открывшие хоть один материал`}
									/>
									<Tile
										label="Открытия"
										value={number.format(c.totals.viewers)}
										hint="человек × материал: кто открыл 10 разных материалов, даёт 10"
									/>
									<Tile
										label="В избранное"
										value={percent(c.totals.favoriteRate)}
										hint={`${number.format(c.totals.favorites)} добавлений, доля от открытий`}
									/>
									<Tile
										label="Досматривают видео"
										value={percent(c.totals.completion)}
										hint="доля начатых видео, досмотренных до 90%"
									/>
									<Tile
										label="Новых материалов"
										value={number.format(c.publishing.created)}
										hint={periodWord}
									/>
								</Grid>
								<Panel
									title="Просмотры и избранное"
									description={`События по ${step === "week" ? "неделям" : "дням"}`}
								>
									<LineChart
										data={c.series}
										series={[
											{ key: "newReaders", label: "Открыли новый для себя материал" },
											{ key: "favorites", label: "Добавили в избранное" },
										]}
										formatDate={formatDate}
										ariaLabel="Открытия новых для человека материалов и добавления в избранное по времени"
									/>
								</Panel>
								<Grid className="lg:grid-cols-5">
									<Panel
										title="Топ материалов"
										description="По уникальным зрителям материала за период"
										className="lg:col-span-3"
									>
										{c.topPosts.length === 0 ? (
											<p className="text-sm text-muted-foreground">
												Материалы за этот период не смотрели
											</p>
										) : (
											<table className="w-full text-left text-sm">
												<thead className="text-xs text-muted-foreground">
													<tr>
														<th className="pb-2 font-medium">Материал</th>
														<th className="pb-2 text-right font-medium">Зрители</th>
														<th className="hidden pb-2 text-right font-medium sm:table-cell">
															Избранное
														</th>
														<th
															className="hidden pb-2 text-right font-medium sm:table-cell"
															title="Какая доля зрителей досмотрела материал до конца"
														>
															До конца
														</th>
													</tr>
												</thead>
												<tbody>
													{c.topPosts.map((post) => (
														<tr key={post.id} className="border-t border-border/70">
															<td className="max-w-0 py-2 pr-3">
																<Link
																	to={`/admin/posts/${post.id}`}
																	className="block truncate hover:[--link-underline:100%] focus-visible:[--link-underline:100%]"
																>
																	<span className="link-underline">
																		{post.title || "Без названия"}
																	</span>
																</Link>
																<div className="mt-1 h-1 overflow-hidden rounded-full bg-secondary">
																	<div
																		className="h-full rounded-r-[4px] bg-[#3987e5]"
																		style={{
																			width: `${(post.viewers / c.topPosts[0]!.viewers) * 100}%`,
																		}}
																	/>
																</div>
															</td>
															<td className="py-2 text-right tabular-nums">
																{number.format(post.viewers)}
															</td>
															<td className="hidden py-2 text-right text-muted-foreground tabular-nums sm:table-cell">
																{percent(post.favoriteRate)}
															</td>
															<td className="hidden py-2 text-right text-muted-foreground tabular-nums sm:table-cell">
																{percent(post.completion)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										)}
									</Panel>
									<Panel
										title="Ниши"
										description="Открытий на один материал ниши"
										className="lg:col-span-2"
									>
										<BarList
											items={c.byNiche.slice(0, 10).map((niche) => ({
												key: niche.id,
												label: `${niche.name} · ${niche.posts}`,
												value: Math.round(niche.viewersPerPost),
												hint: `${niche.viewers} открытий на ${niche.posts} материалов`,
											}))}
										/>
									</Panel>
								</Grid>
								{c.deadPosts.length ? (
									<Panel
										title="Материалы без зрителей"
										description="Опубликованы больше месяца назад и почти никем не открыты. Их стоит переупаковать или поднять в канале."
									>
										<ul className="divide-y divide-border/70">
											{c.deadPosts.map((post) => (
												<li
													key={post.id}
													className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
												>
													<Link
														to={`/admin/posts/${post.id}`}
														className="min-w-0 flex-1 truncate hover:[--link-underline:100%] focus-visible:[--link-underline:100%]"
													>
														<span className="link-underline">{post.title || "Без названия"}</span>
													</Link>
													<span className="shrink-0 text-xs text-muted-foreground tabular-nums">
														{post.viewers} зр. · {formatShortDate(post.createdAt)}
													</span>
													<span className="flex shrink-0 gap-1">
														<Link
															to={`/admin/posts/${post.id}#publish`}
															className="rounded-full border border-border px-2.5 py-0.5 text-xs transition-colors hover:bg-secondary"
														>
															Поднять в канале
														</Link>
														<Link
															to={`/admin/edit/${post.id}`}
															className="rounded-full border border-border px-2.5 py-0.5 text-xs transition-colors hover:bg-secondary"
														>
															Переупаковать
														</Link>
													</span>
												</li>
											))}
										</ul>
									</Panel>
								) : null}
							</Stack>
						</IntroContext.Provider>
					)}
				</LoadSwap>
			</div>
		</PageShell>
	)
}
